import TagIcon from '@jetbrains/icons/tag.svg?react'
import Link from '@jetbrains/ring-ui-built/components/link/link'
import type {TagRenderProps} from '@jetbrains/ring-ui-built/components/tag/tag'
import Tag, {TagType} from '@jetbrains/ring-ui-built/components/tag/tag'
import {SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import classnames from 'classnames'
import type {ReactNode, SyntheticEvent} from 'react'
import * as React from 'react'
import {useLocation} from 'react-router'

import {getBuildTypeHref, getPipelineHref} from '../../../routes'
import {getIsSakuraUI} from '../../../selectors'
import type {BuildId, BuildTypeId, PipelineId} from '../../../types'
import {resolveWebEntityLink, WebEntityType} from '../../../utils/entityLinks'
import {isBuildFilterDropdownEnabled} from '../../../utils/featureToggles'
import {parseTags, serializeTags} from '../../../utils/queryParams'
import {Modes} from '../../pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeOverviewTab.modes'
import {removeBuildTag} from '../BuildActionsDropdown/TagDialog/TagDialog.actions'
import MiddleEllipsis from '../MiddleEllipsis/MiddleEllipsis'
import RouterLink from '../RouterLink/RouterLink'

import styles from './BuildTag.module.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  onSelect?: (() => unknown) | undefined
  className?: string
  buildTypeId?: BuildTypeId | undefined
  buildId?: BuildId | undefined
  label?: string | null | undefined
  multiple?: boolean
  selected?: boolean
  isPipelineStyle?: boolean
  href?: string
  removable?: boolean
  pipelineId?: PipelineId | null
}

const toggleTag = (currentTags: readonly string[], label: string, selected: boolean): string[] =>
  selected ? currentTags.filter(t => t !== label) : [...currentTags, label]

const useHref = ({href, label, buildTypeId, pipelineId, selected = false}: Props) => {
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const baseHref = pipelineId
    ? getPipelineHref(pipelineId)
    : (resolveWebEntityLink(WebEntityType.BUILD_TYPE, buildTypeId, isSakuraUI) ?? '')
  const location = useLocation()

  if (href != null) {
    return href
  }

  if (label == null) {
    return '#'
  }

  const params = new URLSearchParams(location.search)
  if (pipelineId == null) {
    params.set('tab', 'buildTypeHistoryList')
  }

  if (isBuildFilterDropdownEnabled) {
    const currentTags = parseTags(params.get('tags'), params.get('tag'))
    const nextTags = toggleTag(currentTags, label, selected)
    params.delete('tag')
    params.delete('tags')
    const serialized = serializeTags(nextTags)
    if (serialized != null) {
      params.set('tags', serialized)
    }
  } else if (selected) {
    params.delete('tag')
    params.delete('tags')
  } else {
    params.set('tag', label)
    params.delete('tags')
  }

  const separator = baseHref.includes('?') ? '&' : '?'
  return `${baseHref}${separator}${params.toString()}`
}

function BuildTag(props: Props) {
  const dispatch = useAppDispatch()
  const {
    className,
    label,
    multiple,
    selected = false,
    buildTypeId,
    onSelect,
    removable,
    buildId,
    pipelineId,
  } = props
  const stopPropagation = React.useCallback((e: SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const href = useHref(props)
  const location = useLocation()
  const classes = classnames(className, {
    [styles.multiple]: multiple,
  })

  if (label == null) {
    return null
  }

  if (multiple === true) {
    return (
      <span
        className={classes}
        onClick={stopPropagation}
        role="presentation"
        data-test="build-tag-multiple"
      >
        <span>
          <SvgIcon icon={TagIcon} className={styles.icon} />
        </span>
        {label}
      </span>
    )
  }

  let render: ((props: TagRenderProps) => ReactNode) | undefined
  if (onSelect != null) {
    render = undefined
  } else if (isSakuraUI && (buildTypeId != null || pipelineId != null)) {
    const basePath = pipelineId ? getPipelineHref(pipelineId) : getBuildTypeHref(buildTypeId)
    if (isBuildFilterDropdownEnabled) {
      const searchParams = new URLSearchParams(location.search)
      const currentTags = parseTags(searchParams.get('tags'), searchParams.get('tag'))
      const nextTags = toggleTag(currentTags, label, selected)
      searchParams.delete('tag')
      searchParams.delete('tags')
      const serialized = serializeTags(nextTags)
      if (serialized != null) {
        searchParams.set('tags', serialized)
      }
      searchParams.set('mode', Modes.BUILDS)
      const fullTo = `${basePath}?${searchParams.toString()}`
      render = ({children, ref, ...tagProps}) => (
        <RouterLink
          to={fullTo}
          {...tagProps}
          className={classnames(styles.link, tagProps.className)}
        >
          {children}
        </RouterLink>
      )
    } else {
      // Single-tag: select this tag or deselect if already selected
      render = ({children, ref, ...tagProps}) => (
        <RouterLink
          to={basePath}
          params={{tag: selected ? null : label, tags: null, mode: Modes.BUILDS}}
          withMergeParams
          {...tagProps}
          className={classnames(styles.link, tagProps.className)}
        >
          {children}
        </RouterLink>
      )
    }
  } else {
    render = ({children, ref, ...tagProps}) => (
      <Link href={href} {...tagProps}>
        {children}
      </Link>
    )
  }

  return (
    <Tag
      tagType={selected ? TagType.MAIN : TagType.DEFAULT}
      containerClassName={classes}
      className={styles.tag}
      onClick={onSelect}
      onRemove={
        buildId != null ? () => dispatch(removeBuildTag(buildId, label, buildTypeId)) : undefined
      }
      readOnly={!removable || buildId == null}
      render={render}
      data-test="build-tag"
    >
      <MiddleEllipsis tailLength={2}>{label}</MiddleEllipsis>
    </Tag>
  )
}

export default React.memo(BuildTag)
